import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Loader2, History, Copy, Check } from "lucide-react";
import { getErrorMessage } from "../../utils/errorHandler";
import { API, authHeader } from "./shared";
import { EXAMPLE_COMPANION, EmptyState } from "./_shared_ui";

export default function CompanionPanel({ t }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    try {
      const res = await axios.get(`${API}/api/pulpit/companion/sessions`, { headers: authHeader() });
      setSessions(res.data.sessions || []);
    } catch { /* silent */ }
  };

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/api/pulpit/companion/history/${sid}`, { headers: authHeader() });
      setSessionId(sid);
      setMessages(res.data.messages || []);
    } catch (err) {
      toast.error(getErrorMessage(err, t("load_failed") || "Load failed"));
    }
  };

  useEffect(() => { loadSessions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", text: userMsg }]);
    try {
      const res = await axios.post(`${API}/api/pulpit/companion`, {
        message: userMsg, session_id: sessionId,
      }, { headers: authHeader() });
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply }]);
      loadSessions();
    } catch (err) {
      toast.error(getErrorMessage(err, t("send_failed") || "Send failed"));
      setMessages((m) => m.slice(0, -1));
      setInput(userMsg);
    } finally { setSending(false); }
  };

  const startNew = () => { setSessionId(null); setMessages([]); };

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-4">
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-1"><History className="w-3.5 h-3.5" />{t("sessions") || "Sessions"}</CardTitle>
            <Button variant="ghost" size="sm" onClick={startNew} data-testid="new-companion-btn">
              + {t("new") || "New"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-2">
          <ScrollArea className="h-[450px]">
            {sessions.length === 0 ? (
              <div className="text-xs text-slate-500 text-center py-6">{t("no_sessions") || "No sessions yet."}</div>
            ) : (
              <div className="space-y-1">
                {sessions.map((s) => (
                  <button key={s.session_id} onClick={() => loadSession(s.session_id)}
                    className={`w-full text-left p-2 rounded text-xs hover:bg-amber-500/10 ${sessionId === s.session_id ? "bg-amber-500/10 border border-amber-500/30" : ""}`}
                    data-testid={`session-${s.session_id}`}>
                    <div className="truncate font-medium text-slate-200">{s.last_message || "—"}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{s.message_count} {t("messages_count") || "msgs"}</div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/60 border-slate-800 flex flex-col">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t("sermon_companion") || "Sermon Companion"}</CardTitle>
          <CardDescription>{t("companion_desc") || "Ask Bible, theology, or sermon-prep questions"}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          <div ref={scrollRef} className="h-[400px] overflow-y-auto space-y-3 mb-3 pr-2" data-testid="companion-messages">
            {messages.length === 0 && (
              <EmptyState
                icon={Loader2}
                title={t("companion_empty_title") || "Ask me anything biblical"}
                description={t("companion_start") || "Scripture, theology, Greek/Hebrew word studies, sermon ideas — I'm built for deep prep."}
                examples={EXAMPLE_COMPANION}
                onPick={(ex) => setInput(ex.text)}
                testId="companion-empty"
              />
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} group`}>
                <div className={`max-w-[80%] p-3 rounded-2xl text-sm whitespace-pre-wrap relative ${m.role === "user" ? "bg-amber-500/20 border border-amber-500/30 text-amber-50" : "bg-slate-800/80 text-slate-100"}`}>
                  {m.text}
                  {m.role === "assistant" && (
                    <button
                      onClick={async () => {
                        try { await navigator.clipboard.writeText(m.text); toast.success("Copied"); } catch { /* ignore */ }
                      }}
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-amber-300 p-1"
                      aria-label="Copy message"
                      data-testid={`copy-companion-msg-${i}`}
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-slate-800/80 p-3 rounded-2xl text-sm flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {t("thinking") || "thinking..."}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-auto">
            <Input data-testid="companion-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
              placeholder={t("ask_question") || "Ask a question..."} disabled={sending} />
            <Button onClick={send} disabled={sending || !input.trim()} data-testid="send-companion-btn" className="bg-amber-600 hover:bg-amber-700">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
